import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { BusinessIdea } from '../../services/businessIdeaTypes';
import { BusinessPlan as BusinessPlanType, BusinessPlanSection, SectionId, SECTION_METADATA } from '../../types/businessPlan';
import { getSavedBusinessIdeas, getUserBusinessPlansFromSupabase, saveBusinessPlan } from '../../services/savedIdeasService';
import { getBusinessPlanFromSupabase } from '../../services/supabase/businessPlanService';
import { ENV } from '../../config/env';
import { supabase } from '../../config/supabaseClient';
import { useUsage } from '../../contexts/UsageContext';
import { UsageBanner } from '../../components/common/UsageBanner';
import BusinessPlanDocument from '../../components/business-plan/BusinessPlanDocument';
import GuidedSectionModal from '../../components/business-plan/GuidedSectionModal';
import BusinessPlanPDFExport from '../../components/business-plan/BusinessPlanPDFExport';
import BusinessPlanDOCXExport from '../../components/business-plan/BusinessPlanDOCXExport';
import PlanQualityScorecard from '../../components/business-plan/PlanQualityScorecard';
import BusinessPlanShareModal from '../../components/business-plan/BusinessPlanShareModal';
import BusinessPlanCollaboratorsModal from '../../components/business-plan/BusinessPlanCollaboratorsModal';
import VersionHistory from '../../components/business-plan/VersionHistory';
import BusinessPlanHubHeader from './components/BusinessPlanHubHeader';
import BusinessPlanCommandPalette from './components/BusinessPlanCommandPalette';
import PlansTab from './tabs/PlansTab';
import IdeasTab from './tabs/IdeasTab';
import TemplatesTab from './tabs/TemplatesTab';
import { applyTemplateToBusinessPlan, getTemplate } from '../../services/industryTemplateService';

type TabId = 'plans' | 'ideas' | 'templates';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  component: React.ComponentType;
}

const TABS: Tab[] = [
  { id: 'plans', label: 'My Plans', icon: '📄', component: PlansTab },
  { id: 'ideas', label: 'Ideas', icon: '💡', component: IdeasTab },
  { id: 'templates', label: 'Templates', icon: '📋', component: TemplatesTab }
];

const BusinessPlanHub: React.FC = () => {
  const { user } = useAuth();
  const { stats: usageStats, refresh: refreshUsage } = useUsage();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  const [activeTab, setActiveTab] = useState<TabId>('plans');
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [savedPlans, setSavedPlans] = useState<BusinessPlanType[]>([]);
  const [savedIdeas, setSavedIdeas] = useState<BusinessIdea[]>([]);
  const [showGettingStarted, setShowGettingStarted] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('bp_hub_getting_started_dismissed') !== 'true';
    } catch {
      return true;
    }
  });
  const [workspacePlan, setWorkspacePlan] = useState<BusinessPlanType | null>(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [workspaceUsage, setWorkspaceUsage] = useState<{ current: number; limit: number; remaining: number; warning?: string } | null>(null);
  const [activeWorkspaceSectionId, setActiveWorkspaceSectionId] = useState<SectionId | null>(null);
  const [guidedSectionId, setGuidedSectionId] = useState<SectionId | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCollaboratorsModalOpen, setIsCollaboratorsModalOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  const [versionHistoryPlans, setVersionHistoryPlans] = useState<BusinessPlanType[]>([]);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [changelogLoading, setChangelogLoading] = useState(false);
  const [changelogEvents, setChangelogEvents] = useState<any[]>([]);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggestionsStatusFilter, setSuggestionsStatusFilter] = useState<'open' | 'applied' | 'rejected'>('open');
  const [suggestionsSectionFilter, setSuggestionsSectionFilter] = useState<string>('');
  const [newSuggestionSectionId, setNewSuggestionSectionId] = useState<string>('');
  const [newSuggestionOriginalText, setNewSuggestionOriginalText] = useState<string>('');
  const [newSuggestionSuggestedText, setNewSuggestionSuggestedText] = useState<string>('');
  const [newSuggestionComment, setNewSuggestionComment] = useState<string>('');
  const [canEditPlan, setCanEditPlan] = useState<boolean>(true);
  const [isOutlineOpen, setIsOutlineOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('bp_outline_collapsed') !== 'true';
    } catch {
      return true;
    }
  });

  type BusinessPlanVersionRow = {
    id: string;
    business_plan_id: string;
    version_number: number;
    created_by: string;
    comment: string | null;
    snapshot: any;
    created_at: string;
  };

  type BusinessPlanSuggestionRow = {
    id: string;
    business_plan_id: string;
    section_id: string;
    author_user_id: string | null;
    status: 'open' | 'applied' | 'rejected';
    original_text: string | null;
    suggested_text: string;
    comment: string | null;
    resolved_by: string | null;
    resolved_at: string | null;
    created_at: string;
    updated_at: string;
  };

  const loadSuggestions = async (args: {
    planId: string;
    status?: 'open' | 'applied' | 'rejected';
    sectionId?: string;
  }) => {
    setSuggestionsLoading(true);
    try {
      let q = supabase
        .from('business_plan_suggestions')
        .select('id,business_plan_id,section_id,author_user_id,status,original_text,suggested_text,comment,resolved_by,resolved_at,created_at,updated_at')
        .eq('business_plan_id', args.planId)
        .order('created_at', { ascending: false });

      if (args.status) q = q.eq('status', args.status);
      if (args.sectionId) q = q.eq('section_id', args.sectionId);

      const { data, error } = await q;
      if (error) throw error;

      const rows = (Array.isArray(data) ? data : []) as BusinessPlanSuggestionRow[];
      setSuggestions(rows);
    } catch (e: any) {
      console.error('Failed to load suggestions:', e);
      toast.error(e?.message || 'Failed to load suggestions');
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const fetchCanEditPlan = async (planId: string) => {
    try {
      const { data, error } = await supabase.rpc('can_edit_business_plan', {
        p_business_plan_id: planId,
      });
      if (error) throw error;
      setCanEditPlan(!!data);
    } catch (e) {
      console.error('Failed to determine edit permissions:', e);
      setCanEditPlan(true);
    }
  };

  useEffect(() => {
    if (!workspacePlan?.id) return;
    void fetchCanEditPlan(workspacePlan.id);
    void loadSuggestions({ planId: workspacePlan.id, status: 'open' });
  }, [workspacePlan?.id]);

  const createSuggestion = async () => {
    if (!workspacePlan?.id) return;
    try {
      const sessionResp = await supabase.auth.getSession();
      const userId = sessionResp?.data?.session?.user?.id;
      if (!userId) {
        toast.error('You must be logged in');
        return;
      }

      const sectionId = (newSuggestionSectionId || '').trim();
      const suggestedText = (newSuggestionSuggestedText || '').trim();
      if (!sectionId) throw new Error('Section is required');
      if (!suggestedText) throw new Error('Suggested text is required');

      const originalText = (newSuggestionOriginalText || '').trim();
      const comment = (newSuggestionComment || '').trim();

      const { error } = await supabase.from('business_plan_suggestions').insert({
        business_plan_id: workspacePlan.id,
        section_id: sectionId,
        author_user_id: userId,
        status: 'open',
        original_text: originalText || null,
        suggested_text: suggestedText,
        comment: comment || null,
      });

      if (error) throw error;
      toast.success('Suggestion created');
      setNewSuggestionOriginalText('');
      setNewSuggestionSuggestedText('');
      setNewSuggestionComment('');
      await loadSuggestions({
        planId: workspacePlan.id,
        status: suggestionsStatusFilter,
        sectionId: suggestionsSectionFilter || undefined,
      });
    } catch (e: any) {
      console.error('Failed to create suggestion:', e);
      toast.error(e?.message || 'Failed to create suggestion');
    }
  };

  const rejectSuggestion = async (s: BusinessPlanSuggestionRow) => {
    if (!workspacePlan?.id) return;
    if (!canEditPlan) {
      toast.error('Editor access required');
      return;
    }
    try {
      const sessionResp = await supabase.auth.getSession();
      const userId = sessionResp?.data?.session?.user?.id;
      const { error } = await supabase
        .from('business_plan_suggestions')
        .update({
          status: 'rejected',
          resolved_by: userId || null,
          resolved_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', s.id);
      if (error) throw error;

      await logBusinessPlanEvent({
        planId: workspacePlan.id,
        eventType: 'suggestion_rejected',
        sectionId: s.section_id,
        payload: { suggestionId: s.id },
      });

      toast.success('Suggestion rejected');
      await loadSuggestions({
        planId: workspacePlan.id,
        status: suggestionsStatusFilter,
        sectionId: suggestionsSectionFilter || undefined,
      });
    } catch (e: any) {
      console.error('Failed to reject suggestion:', e);
      toast.error(e?.message || 'Failed to reject suggestion');
    }
  };

  const applySuggestion = async (s: BusinessPlanSuggestionRow) => {
    if (!workspacePlan?.id) return;
    if (!canEditPlan) {
      toast.error('Editor access required');
      return;
    }
    try {
      const sectionId = s.section_id as SectionId;
      const current = typeof (workspacePlan.sections as any)?.[sectionId]?.content === 'string'
        ? (workspacePlan.sections as any)[sectionId].content
        : '';

      let nextContent = current;
      const original = (s.original_text || '').trim();
      const suggested = (s.suggested_text || '').trim();
      if (!suggested) throw new Error('Suggestion has no suggested text');

      if (original && current.includes(original)) {
        nextContent = current.replace(original, suggested);
      } else {
        nextContent = suggested;
      }

      const nextPlan = {
        ...workspacePlan,
        sections: {
          ...(workspacePlan.sections || {}),
          [sectionId]: {
            ...(workspacePlan.sections as any)?.[sectionId],
            id: sectionId,
            title: (workspacePlan.sections as any)?.[sectionId]?.title || (SECTION_METADATA as any)?.[sectionId]?.title,
            content: nextContent,
            status: 'completed',
            pageNumber: (workspacePlan.sections as any)?.[sectionId]?.pageNumber || (SECTION_METADATA as any)?.[sectionId]?.pageNumber,
          },
        },
        updated_at: new Date().toISOString(),
      } as any;

      const saved = await saveBusinessPlan(nextPlan);
      setWorkspacePlan(saved as BusinessPlanType);
      await createVersionSnapshot({ plan: saved as any });

      const sessionResp = await supabase.auth.getSession();
      const userId = sessionResp?.data?.session?.user?.id;
      const { error } = await supabase
        .from('business_plan_suggestions')
        .update({
          status: 'applied',
          resolved_by: userId || null,
          resolved_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', s.id);
      if (error) throw error;

      await logBusinessPlanEvent({
        planId: workspacePlan.id,
        eventType: 'suggestion_applied',
        sectionId: s.section_id,
        payload: { suggestionId: s.id },
      });

      toast.success('Suggestion applied');
      await loadSuggestions({
        planId: workspacePlan.id,
        status: suggestionsStatusFilter,
        sectionId: suggestionsSectionFilter || undefined,
      });
    } catch (e: any) {
      console.error('Failed to apply suggestion:', e);
      toast.error(e?.message || 'Failed to apply suggestion');
    }
  };

  const shouldCreateSnapshot = async (planId: string) => {
    try {
      const { data, error } = await supabase
        .from('business_plan_versions')
        .select('created_at')
        .eq('business_plan_id', planId)
        .order('version_number', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) return true;
      const last = (data as any)?.created_at as string | undefined;
      if (!last) return true;
      const lastMs = new Date(last).getTime();
      if (!Number.isFinite(lastMs)) return true;
      return Date.now() - lastMs > 5 * 60 * 1000;
    } catch {
      return true;
    }
  };

  const loadVersionHistory = async (planId: string) => {
    setVersionHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('business_plan_versions')
        .select('id,business_plan_id,version_number,created_by,comment,snapshot,created_at')
        .eq('business_plan_id', planId)
        .order('version_number', { ascending: false });

      if (error) throw error;

      const rows = (Array.isArray(data) ? data : []) as BusinessPlanVersionRow[];
      const plans: BusinessPlanType[] = rows
        .map((row) => {
          const snap = row.snapshot || {};

          const sections = (snap as any)?.sections || {};
          const contentFromSections: Record<string, string> = {};
          if (sections && typeof sections === 'object') {
            Object.entries(sections as Record<string, any>).forEach(([sectionId, sec]) => {
              const txt = typeof (sec as any)?.content === 'string' ? (sec as any).content : '';
              contentFromSections[sectionId] = txt;
            });
          }

          return {
            ...(snap as any),
            id: snap.id || row.business_plan_id,
            version: row.version_number,
            content: (snap as any)?.content || contentFromSections,
            created_at: row.created_at,
            updated_at: row.created_at,
          } as BusinessPlanType;
        })
        .filter(Boolean);

      setVersionHistoryPlans(plans);
    } catch (e: any) {
      console.error('Failed to load version history:', e);
      toast.error(e?.message || 'Failed to load version history');
      setVersionHistoryPlans([]);
    } finally {
      setVersionHistoryLoading(false);
    }
  };

  const createVersionSnapshot = async (args: { plan: BusinessPlanType; comment?: string }) => {
    const { plan, comment } = args;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        toast.error('You must be logged in');
        return;
      }

      const okToSnapshot = await shouldCreateSnapshot(plan.id);
      if (!okToSnapshot) {
        return;
      }

      const { data: existing, error: existingError } = await supabase
        .from('business_plan_versions')
        .select('version_number')
        .eq('business_plan_id', plan.id)
        .order('version_number', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingError) throw existingError;
      const nextVersionNumber = ((existing as any)?.version_number || 0) + 1;

      const { error } = await supabase.from('business_plan_versions').insert({
        business_plan_id: plan.id,
        version_number: nextVersionNumber,
        created_by: userId,
        comment: comment || null,
        snapshot: plan as any,
      });

      if (error) throw error;

      toast.success('Version saved');
    } catch (e: any) {
      console.error('Failed to create version snapshot:', e);
      toast.error(e?.message || 'Failed to save version');
    }
  };

  type BusinessPlanEventRow = {
    id: string;
    business_plan_id: string;
    actor_user_id: string | null;
    event_type: string;
    section_id: string | null;
    payload: any;
    created_at: string;
  };

  const logBusinessPlanEvent = async (args: {
    planId: string;
    eventType: string;
    sectionId?: string;
    payload?: any;
  }) => {
    try {
      const sessionResp = await supabase.auth.getSession();
      const actorUserId = sessionResp?.data?.session?.user?.id;
      if (!actorUserId) return;

      const insertPayload = args.payload && typeof args.payload === 'object' ? args.payload : {};

      const { error } = await supabase.from('business_plan_events').insert({
        business_plan_id: args.planId,
        actor_user_id: actorUserId,
        event_type: args.eventType,
        section_id: args.sectionId || null,
        payload: insertPayload,
      });

      if (error) throw error;
    } catch (e) {
      console.error('Failed to log business plan event:', e);
    }
  };

  const loadChangelog = async (planId: string) => {
    setChangelogLoading(true);
    try {
      const { data, error } = await supabase
        .from('business_plan_events')
        .select('id,business_plan_id,actor_user_id,event_type,section_id,payload,created_at')
        .eq('business_plan_id', planId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;

      const rows = (Array.isArray(data) ? data : []) as BusinessPlanEventRow[];
      setChangelogEvents(rows);
    } catch (e: any) {
      console.error('Failed to load changelog:', e);
      toast.error(e?.message || 'Failed to load changelog');
      setChangelogEvents([]);
    } finally {
      setChangelogLoading(false);
    }
  };

  const restoreVersion = async (versionNumber: number) => {
    if (!workspacePlan) return;
    setWorkspaceLoading(true);
    try {
      const { data, error } = await supabase
        .from('business_plan_versions')
        .select('snapshot')
        .eq('business_plan_id', workspacePlan.id)
        .eq('version_number', versionNumber)
        .maybeSingle();

      if (error) throw error;
      const snap = (data as any)?.snapshot;
      if (!snap) {
        toast.error('Version not found');
        return;
      }

      const next = {
        ...(workspacePlan as any),
        ...(snap as any),
        id: workspacePlan.id,
        updated_at: new Date().toISOString(),
      } as any;

      const saved = await saveBusinessPlan(next);
      setWorkspacePlan(saved as BusinessPlanType);
      toast.success('Version restored');
      await logBusinessPlanEvent({
        planId: workspacePlan.id,
        eventType: 'version_restored',
        payload: { versionNumber },
      });
      try {
        window.dispatchEvent(new Event('bp-data-changed'));
      } catch {
      }
    } catch (e: any) {
      console.error('Failed to restore version:', e);
      toast.error(e?.message || 'Failed to restore version');
    } finally {
      setWorkspaceLoading(false);
      setIsVersionHistoryOpen(false);
    }
  };

  const isNewRoute = useMemo(() => {
    return location.pathname === '/business-plan/new';
  }, [location.pathname]);

  const workspacePlanId = useMemo(() => {
    return (params as any)?.id as string | undefined;
  }, [params]);

  const createEmptySections = useMemo(() => {
    const sections: Record<SectionId, BusinessPlanSection> = {} as Record<SectionId, BusinessPlanSection>;
    Object.entries(SECTION_METADATA).forEach(([id, metadata]) => {
      sections[id as SectionId] = {
        id: id as SectionId,
        title: metadata.title,
        content: '',
        status: 'not-started',
        pageNumber: metadata.pageNumber || 0,
        subsections: [],
        error: undefined,
      };
    });
    return sections;
  }, []);

  const outlineSections = useMemo(() => {
    return Object.entries(SECTION_METADATA)
      .filter(([sectionId]) => sectionId !== 'cover-page' && sectionId !== 'table-of-contents')
      .map(([sectionId, metadata]) => ({
        id: sectionId as SectionId,
        title: metadata.title,
      }));
  }, []);

  const workspaceProgress = useMemo(() => {
    if (!workspacePlan?.sections) {
      return { completed: 0, total: outlineSections.length, percent: 0 };
    }
    const total = outlineSections.length;
    const completed = outlineSections.filter(s => {
      const sec: any = (workspacePlan.sections as any)?.[s.id];
      const content = typeof sec?.content === 'string' ? sec.content : '';
      const status = sec?.status;
      return status === 'completed' && content.trim().length > 0;
    }).length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percent };
  }, [outlineSections, workspacePlan?.sections]);

  const refreshHubData = async () => {
    if (!user?.id) return;
    const [plans, ideas] = await Promise.all([
      getUserBusinessPlansFromSupabase(),
      getSavedBusinessIdeas(),
    ]);
    setSavedPlans(plans as BusinessPlanType[]);
    setSavedIdeas(ideas);
  };

  useEffect(() => {
    if (!workspacePlanId) return;
    if (!workspacePlan) return;
    if (workspaceLoading) return;

    let obs: IntersectionObserver | null = null;
    const t = window.setTimeout(() => {
      const nodes = outlineSections
        .map(s => document.getElementById(`section-${s.id}`))
        .filter(Boolean) as HTMLElement[];

      if (nodes.length === 0) return;

      obs = new IntersectionObserver(
        (entries) => {
          const best = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];
          if (!best) return;

          const idAttr = (best.target as HTMLElement).id;
          const sectionId = idAttr.replace('section-', '') as SectionId;
          setActiveWorkspaceSectionId(sectionId);
        },
        {
          root: null,
          threshold: [0.1, 0.25, 0.5],
          rootMargin: '-25% 0px -65% 0px',
        }
      );

      const observer = obs;
      if (observer) nodes.forEach(n => observer.observe(n));
    }, 50);

    return () => {
      window.clearTimeout(t);
      obs?.disconnect();
    };
  }, [outlineSections, workspaceLoading, workspacePlan, workspacePlanId]);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoading(true);
        await refreshHubData();
      } catch (err: any) {
        console.error('Failed to load data:', err);
        toast.error(err?.message || 'Failed to load business plans');
      } finally {
        setIsLoading(false);
      }
    };

    void loadData();
  }, [user?.id]);

  useEffect(() => {
    const handler = () => {
      void refreshHubData();
    };

    window.addEventListener('bp-data-changed', handler);
    return () => window.removeEventListener('bp-data-changed', handler);
  }, [user?.id]);

  // Set document title
  useEffect(() => {
    document.title = 'Business Plan Generator | Explanatum';
  }, []);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Parse active tab from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.find(t => t.id === hash)) {
      setActiveTab(hash as TabId);
    }
  }, [location.hash]);

  // Hub-only deep links: /business-plan/new and /business-plan/:id
  useEffect(() => {
    const state = (location as any)?.state;
    const templateId = state?.templateId as string | undefined;

    // /business-plan/new should guide users into creation
    if (isNewRoute) {
      const selectedIdea = state?.selectedIdea as BusinessIdea | undefined;

      // If an idea was passed (from IdeasTab), create a draft plan immediately
      if (selectedIdea?.id) {
        const run = async () => {
          try {
            setWorkspaceError(null);
            setWorkspaceLoading(true);
            const draftPlan: Partial<BusinessPlanType> = {
              id: '',
              title: `${selectedIdea.title} Business Plan`,
              idea_id: selectedIdea.id,
              sections: createEmptySections,
              status: 'draft',
              version: 1,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              businessIdea: selectedIdea,
            };

            if (templateId) {
              const template = await getTemplate(templateId);
              if (template) {
                applyTemplateToBusinessPlan(draftPlan as any, template as any, { overwriteExisting: true });
              }
            }
            const saved = await saveBusinessPlan(draftPlan);
            try {
              window.dispatchEvent(new Event('bp-data-changed'));
            } catch {
            }
            navigate(`/business-plan/${saved.id}`, { replace: true });
          } catch (err: any) {
            console.error('Failed to create draft plan:', err);
            toast.error(err?.message || 'Failed to create plan');
          } finally {
            setWorkspaceLoading(false);
          }
        };
        void run();
        return;
      }

      // Otherwise, default to the Ideas tab so the user can pick an idea
      setActiveTab('ideas');
      if (location.hash !== '#ideas') {
        navigate({ pathname: location.pathname, hash: '#ideas' }, { replace: true, state });
      }
    }
  }, [isNewRoute, location, navigate, createEmptySections]);

  // Load workspace plan if /business-plan/:id
  useEffect(() => {
    if (!workspacePlanId) {
      setWorkspacePlan(null);
      setWorkspaceError(null);
      return;
    }

    const run = async () => {
      try {
        setWorkspaceLoading(true);
        setWorkspaceError(null);
        const plan = await getBusinessPlanFromSupabase(workspacePlanId);
        if (!plan) {
          setWorkspacePlan(null);
          setWorkspaceError('Business plan not found');
          return;
        }
        setWorkspacePlan(plan as BusinessPlanType);
      } catch (err: any) {
        console.error('Failed to load business plan:', err);
        setWorkspaceError(err?.message || 'Failed to load business plan');
        setWorkspacePlan(null);
      } finally {
        setWorkspaceLoading(false);
      }
    };

    void run();
  }, [workspacePlanId]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // Escape to close command palette
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
          <div className="flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading your business plans...</div>
          </div>
        </div>
      </div>
    );
  }

  const ActiveTabComponent = TABS.find(t => t.id === activeTab)?.component || PlansTab;

  // Workspace view (hub-only)
  if (workspacePlanId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Business Plan</h1>
              <p className="mt-1 text-sm text-gray-600">Edit and export an investor-ready document.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/business-plan')}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Back to Hub
              </button>
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
            {workspaceLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">Loading plan...</div>
            ) : workspaceError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{workspaceError}</div>
            ) : workspacePlan ? (
              <>
                {usageStats?.business_plan ? (
                  <UsageBanner
                    current={usageStats.business_plan.current}
                    limit={usageStats.business_plan.limit}
                    remaining={usageStats.business_plan.remaining}
                    resetsOn={usageStats.business_plan.resetsOn}
                    featureName="business plan generations"
                  />
                ) : null}
                <div className="flex flex-wrap items-center gap-3 pb-4">
                  <button
                    onClick={async () => {
                      if (!workspacePlan?.businessIdea) {
                        toast.error('This plan is missing its business idea context');
                        return;
                      }
                      try {
                        setWorkspaceLoading(true);
                        setWorkspaceError(null);

                        const sessionResp = await supabase.auth.getSession();
                        const token = sessionResp?.data?.session?.access_token || '';

                        const resp = await fetch(`${ENV.API_URL}/api/business-plans/generate-plan`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify({
                            context: {
                              businessIdea: workspacePlan.businessIdea,
                              additionalData: workspacePlan.additionalData,
                              sections: workspacePlan.sections,
                            },
                          }),
                        });

                        const usageCurrent = resp.headers.get('X-Usage-Current');
                        const usageLimit = resp.headers.get('X-Usage-Limit');
                        const usageRemaining = resp.headers.get('X-Usage-Remaining');
                        const usageWarning = resp.headers.get('X-Usage-Warning') || undefined;
                        if (usageCurrent && usageLimit && usageRemaining) {
                          const current = Number.parseInt(usageCurrent, 10);
                          const limit = Number.parseInt(usageLimit, 10);
                          const remaining = Number.parseInt(usageRemaining, 10);
                          if (!Number.isNaN(current) && !Number.isNaN(limit) && !Number.isNaN(remaining)) {
                            setWorkspaceUsage({ current, limit, remaining, warning: usageWarning });
                            if (usageWarning) toast(usageWarning);
                          }
                        }

                        if (!resp.ok) {
                          if (resp.status === 429) {
                            try {
                              const data = await resp.json();
                              const msg = (data && (data.message || data.error)) || 'Monthly limit reached';
                              throw new Error(msg);
                            } catch {
                              const text = await resp.text();
                              throw new Error(text || 'Monthly limit reached');
                            }
                          }
                          const text = await resp.text();
                          throw new Error(text || 'Failed to generate plan');
                        }

                        const generated = await resp.json();
                        const next = {
                          ...(workspacePlan as any),
                          sections: generated.sections,
                          status: 'completed',
                          updated_at: new Date().toISOString(),
                        };
                        const saved = await saveBusinessPlan(next);
                        setWorkspacePlan(saved as BusinessPlanType);
                        await createVersionSnapshot({ plan: saved as any });
                        await logBusinessPlanEvent({
                          planId: saved.id,
                          eventType: 'plan_generated',
                          payload: { sectionsCount: Object.keys((saved as any)?.sections || {}).length },
                        });
                        toast.success('Plan generated');
                        try {
                          await refreshUsage();
                        } catch {
                        }
                        try {
                          window.dispatchEvent(new Event('bp-data-changed'));
                        } catch {
                        }
                      } catch (e: any) {
                        console.error('Failed to generate plan:', e);
                        toast.error(e?.message || 'Failed to generate plan');
                      } finally {
                        setWorkspaceLoading(false);
                      }
                    }}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                    disabled={workspaceLoading}
                    data-tooltip-id="bp-generate-plan"
                    data-tooltip-content="Generate or re-generate the full plan using your selected template + idea"
                  >
                    Generate Plan
                  </button>
                  <Tooltip id="bp-generate-plan" place="bottom" />
                  {workspaceUsage ? (
                    <div className="ml-2 text-xs text-gray-500">
                      {workspaceUsage.remaining}/{workspaceUsage.limit} remaining
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={async () => {
                      if (!workspacePlan) return;
                      setIsShareModalOpen(true);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                  >
                    Share link
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!workspacePlan) return;
                      setIsCollaboratorsModalOpen(true);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                  >
                    Collaborators
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!workspacePlan) return;
                      setIsVersionHistoryOpen(true);
                      await loadVersionHistory(workspacePlan.id);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                    disabled={versionHistoryLoading}
                  >
                    Versions
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!workspacePlan) return;
                      setIsChangelogOpen(true);
                      await loadChangelog(workspacePlan.id);
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                    disabled={changelogLoading}
                  >
                    Changelog
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!workspacePlan) return;
                      setIsSuggestionsOpen(true);
                      setSuggestionsSectionFilter('');
                      setNewSuggestionSectionId('');
                      await loadSuggestions({ planId: workspacePlan.id, status: suggestionsStatusFilter });
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                    disabled={suggestionsLoading}
                  >
                    Suggestions
                  </button>
                  <BusinessPlanPDFExport plan={workspacePlan} variant="button" />
                  <BusinessPlanDOCXExport plan={workspacePlan} />
                </div>

                <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
                  <div className="lg:sticky lg:top-6 lg:self-start">
                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-900">Outline</div>
                        <button
                          onClick={() => {
                            const next = !isOutlineOpen;
                            try {
                              window.localStorage.setItem('bp_outline_collapsed', next ? 'false' : 'true');
                            } catch {
                            }
                            setIsOutlineOpen(next);
                          }}
                          className="rounded-md px-2 py-1 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                        >
                          {isOutlineOpen ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {workspaceProgress.completed}/{workspaceProgress.total} complete
                          </span>
                          <span>{workspaceProgress.percent}%</span>
                        </div>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-2 rounded-full bg-blue-600"
                            style={{ width: `${workspaceProgress.percent}%` }}
                          />
                        </div>
                      </div>

                      {isOutlineOpen ? (
                        <div className="mt-3 max-h-[65vh] overflow-auto pr-1">
                          <div className="space-y-1">
                            {outlineSections.map((s, idx) => {
                              const sec: any = (workspacePlan.sections as any)?.[s.id];
                              const content = typeof sec?.content === 'string' ? sec.content : '';
                              const isCompleted = sec?.status === 'completed' && content.trim().length > 0;
                              const isActive = activeWorkspaceSectionId === s.id;
                              return (
                                <div
                                  key={s.id}
                                  className={
                                    `flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-sm transition ` +
                                    (isActive ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700')
                                  }
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const el = document.getElementById(`section-${s.id}`);
                                      if (el) {
                                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        setActiveWorkspaceSectionId(s.id);
                                      }
                                    }}
                                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                                    data-tooltip-id={`bp-outline-${s.id}`}
                                    data-tooltip-content="Jump to section"
                                  >
                                    <span className="mt-0.5 w-5 text-xs text-gray-400">{idx + 1}</span>
                                    <span
                                      className={
                                        `mt-1 h-2 w-2 flex-shrink-0 rounded-full ` +
                                        (isCompleted ? 'bg-green-500' : 'bg-gray-300')
                                      }
                                    />
                                    <span className="min-w-0 flex-1 truncate">{s.title}</span>
                                  </button>

                                  <div className="flex flex-shrink-0 items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => setGuidedSectionId(s.id as any)}
                                      className={
                                        'rounded-md px-2 py-1 text-xs font-semibold transition ' +
                                        (isActive ? 'text-blue-700 hover:bg-blue-100' : 'text-gray-600 hover:bg-gray-100')
                                      }
                                      data-tooltip-id={`bp-outline-guided-${s.id}`}
                                      data-tooltip-content="Guided questions for this section"
                                    >
                                      Guided
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const el = document.getElementById(`section-${s.id}`);
                                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        document.dispatchEvent(
                                          new CustomEvent('editSection', { detail: { sectionId: s.id } })
                                        );
                                      }}
                                      className={
                                        'rounded-md px-2 py-1 text-xs font-semibold transition ' +
                                        (isActive ? 'text-blue-700 hover:bg-blue-100' : 'text-gray-600 hover:bg-gray-100')
                                      }
                                      data-tooltip-id={`bp-outline-edit-${s.id}`}
                                      data-tooltip-content="Edit this section"
                                    >
                                      Edit
                                    </button>
                                  </div>

                                  <Tooltip id={`bp-outline-${s.id}`} place="right" />
                                  <Tooltip id={`bp-outline-guided-${s.id}`} place="right" />
                                  <Tooltip id={`bp-outline-edit-${s.id}`} place="right" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    {workspacePlan && workspacePlan.status === 'completed' ? (
                      <PlanQualityScorecard plan={workspacePlan as any} />
                    ) : null}
                    <BusinessPlanDocument
                      plan={workspacePlan}
                      loading={workspaceLoading}
                      error={workspaceError}
                      refreshPlan={async () => {
                        try {
                          setWorkspaceLoading(true);
                          const plan = await getBusinessPlanFromSupabase(workspacePlanId);
                          setWorkspacePlan(plan as BusinessPlanType);
                        } catch (err: any) {
                          setWorkspaceError(err?.message || 'Failed to refresh business plan');
                        } finally {
                          setWorkspaceLoading(false);
                        }
                      }}
                      updateBusinessPlan={canEditPlan ? async (updatedPlan) => {
                        const saved = await saveBusinessPlan({
                          ...(workspacePlan as any),
                          ...(updatedPlan as any),
                          id: workspacePlan.id,
                          updated_at: new Date().toISOString(),
                        });
                        setWorkspacePlan(saved as BusinessPlanType);
                        await createVersionSnapshot({ plan: saved as any });
                        return saved as any;
                      } : undefined}
                      onEditSection={canEditPlan ? async (sectionId, content) => {
                        try {
                          const prevContent =
                            typeof (workspacePlan.sections as any)?.[sectionId]?.content === 'string'
                              ? (workspacePlan.sections as any)[sectionId].content
                              : '';
                          const next = {
                            ...workspacePlan,
                            sections: {
                              ...(workspacePlan.sections || {}),
                              [sectionId]: {
                                ...(workspacePlan.sections as any)?.[sectionId],
                                id: sectionId,
                                title: (workspacePlan.sections as any)?.[sectionId]?.title || SECTION_METADATA[sectionId].title,
                                content,
                                status: 'completed',
                                pageNumber: (workspacePlan.sections as any)?.[sectionId]?.pageNumber || SECTION_METADATA[sectionId].pageNumber,
                              },
                            },
                            updated_at: new Date().toISOString(),
                          } as any;
                          const saved = await saveBusinessPlan(next);
                          setWorkspacePlan(saved as BusinessPlanType);
                          await createVersionSnapshot({ plan: saved as any });
                          await logBusinessPlanEvent({
                            planId: saved.id,
                            eventType: 'section_edited',
                            sectionId,
                            payload: {
                              prevLength: prevContent.length,
                              nextLength: typeof content === 'string' ? content.length : 0,
                            },
                          });
                          toast.success('Section updated');
                          return true;
                        } catch (e: any) {
                          console.error('Failed to save section:', e);
                          toast.error(e?.message || 'Failed to save section');
                          return false;
                        }
                      } : undefined}
                      readOnly={!canEditPlan}
                      suggestions={suggestions}
                      canApplySuggestions={canEditPlan}
                      onOpenSuggestions={(sectionId) => {
                        if (!workspacePlan) return;
                        setIsSuggestionsOpen(true);
                        setSuggestionsSectionFilter(sectionId || '');
                        void loadSuggestions({
                          planId: workspacePlan.id,
                          status: suggestionsStatusFilter,
                          sectionId: sectionId || undefined,
                        });
                      }}
                      onApplySuggestion={(suggestion) => void applySuggestion(suggestion as any)}
                      onRejectSuggestion={(suggestion) => void rejectSuggestion(suggestion as any)}
                    />

                    <BusinessPlanShareModal
                      isOpen={isShareModalOpen && !!workspacePlan}
                      onClose={() => setIsShareModalOpen(false)}
                      planId={workspacePlan.id}
                    />

                    <BusinessPlanCollaboratorsModal
                      isOpen={isCollaboratorsModalOpen && !!workspacePlan}
                      onClose={() => setIsCollaboratorsModalOpen(false)}
                      planId={workspacePlan.id}
                    />

                    <GuidedSectionModal
                      isOpen={!!guidedSectionId}
                      onClose={() => setGuidedSectionId(null)}
                      businessPlanId={workspacePlan.id}
                      sectionId={(guidedSectionId || '') as any}
                      sectionTitle={guidedSectionId ? SECTION_METADATA[guidedSectionId].title : ''}
                      context={{
                        businessIdea: workspacePlan.businessIdea,
                        additionalData: (workspacePlan as any).additionalData,
                        sections: workspacePlan.sections,
                      }}
                      onApplyDraft={async (content) => {
                        if (!guidedSectionId) return;
                        const prevContent =
                          typeof (workspacePlan.sections as any)?.[guidedSectionId]?.content === 'string'
                            ? (workspacePlan.sections as any)[guidedSectionId].content
                            : '';
                        const next = {
                          ...workspacePlan,
                          sections: {
                            ...(workspacePlan.sections || {}),
                            [guidedSectionId]: {
                              ...(workspacePlan.sections as any)?.[guidedSectionId],
                              id: guidedSectionId,
                              title: (workspacePlan.sections as any)?.[guidedSectionId]?.title || SECTION_METADATA[guidedSectionId].title,
                              content,
                              status: 'completed',
                              pageNumber: (workspacePlan.sections as any)?.[guidedSectionId]?.pageNumber || SECTION_METADATA[guidedSectionId].pageNumber,
                            },
                          },
                          updated_at: new Date().toISOString(),
                        } as any;
                        const saved = await saveBusinessPlan(next);
                        setWorkspacePlan(saved as BusinessPlanType);
                        await createVersionSnapshot({ plan: saved as any });
                        await logBusinessPlanEvent({
                          planId: saved.id,
                          eventType: 'guided_draft_applied',
                          sectionId: guidedSectionId,
                          payload: {
                            prevLength: prevContent.length,
                            nextLength: typeof content === 'string' ? content.length : 0,
                          },
                        });
                        try {
                          window.dispatchEvent(new Event('bp-data-changed'));
                        } catch {
                        }
                      }}
                    />

                    {isVersionHistoryOpen ? (
                      <VersionHistory
                        versions={versionHistoryPlans}
                        onRestore={restoreVersion}
                        onClose={() => setIsVersionHistoryOpen(false)}
                      />
                    ) : null}

                    {isChangelogOpen ? (
                      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
                          <div className="flex justify-between items-center mb-4">
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">Changelog</h3>
                              <div className="text-xs text-gray-500">Latest edits, generations, and restores for this plan.</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!workspacePlan) return;
                                  await loadChangelog(workspacePlan.id);
                                }}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                                disabled={changelogLoading}
                              >
                                Refresh
                              </button>
                              <button
                                type="button"
                                onClick={() => setIsChangelogOpen(false)}
                                className="text-gray-400 hover:text-gray-500"
                              >
                                <span className="sr-only">Close</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {changelogLoading ? (
                            <div className="py-10 text-sm text-gray-600">Loading…</div>
                          ) : changelogEvents.length === 0 ? (
                            <div className="py-10 text-sm text-gray-600">No events yet.</div>
                          ) : (
                            <div className="space-y-2">
                              {changelogEvents.map((ev: BusinessPlanEventRow) => {
                                const when = ev.created_at ? new Date(ev.created_at).toLocaleString() : '-';
                                const actor = ev.actor_user_id ? String(ev.actor_user_id).slice(0, 8) : '-';
                                const sectionTitle =
                                  ev.section_id && (SECTION_METADATA as any)?.[ev.section_id]
                                    ? (SECTION_METADATA as any)[ev.section_id].title
                                    : ev.section_id || '';

                                let label = ev.event_type;
                                if (ev.event_type === 'plan_generated') label = 'Plan generated';
                                if (ev.event_type === 'section_edited') label = 'Section edited';
                                if (ev.event_type === 'guided_draft_applied') label = 'Guided draft applied';
                                if (ev.event_type === 'version_restored') label = 'Version restored';

                                return (
                                  <div key={ev.id} className="rounded-lg border border-gray-200 p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-900">{label}</div>
                                        <div className="mt-1 text-xs text-gray-500">
                                          {when} · {actor}
                                          {sectionTitle ? ` · ${sectionTitle}` : ''}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}

                    {isSuggestionsOpen ? (
                      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[85vh] overflow-y-auto">
                          <div className="flex justify-between items-center mb-4">
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">Suggestions</h3>
                              <div className="text-xs text-gray-500">Propose edits per section. Editors can apply/reject.</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setIsSuggestionsOpen(false)}
                              className="text-gray-400 hover:text-gray-500"
                            >
                              <span className="sr-only">Close</span>
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>

                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <div className="text-xs font-semibold text-gray-700">Status</div>
                              <select
                                value={suggestionsStatusFilter}
                                onChange={async (e) => {
                                  const next = e.target.value as any;
                                  setSuggestionsStatusFilter(next);
                                  if (workspacePlan) {
                                    await loadSuggestions({
                                      planId: workspacePlan.id,
                                      status: next,
                                      sectionId: suggestionsSectionFilter || undefined,
                                    });
                                  }
                                }}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              >
                                <option value="open">Open</option>
                                <option value="applied">Applied</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-gray-700">Section</div>
                              <select
                                value={suggestionsSectionFilter}
                                onChange={async (e) => {
                                  const next = e.target.value;
                                  setSuggestionsSectionFilter(next);
                                  if (workspacePlan) {
                                    await loadSuggestions({
                                      planId: workspacePlan.id,
                                      status: suggestionsStatusFilter,
                                      sectionId: next || undefined,
                                    });
                                  }
                                }}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              >
                                <option value="">All</option>
                                {Object.entries(SECTION_METADATA)
                                  .filter(([id]) => id !== 'cover-page' && id !== 'table-of-contents')
                                  .map(([id, meta]) => (
                                    <option key={id} value={id}>{meta.title}</option>
                                  ))}
                              </select>
                            </div>
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!workspacePlan) return;
                                  await loadSuggestions({
                                    planId: workspacePlan.id,
                                    status: suggestionsStatusFilter,
                                    sectionId: suggestionsSectionFilter || undefined,
                                  });
                                }}
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                                disabled={suggestionsLoading}
                              >
                                Refresh
                              </button>
                            </div>
                          </div>

                          <div className="mt-6 rounded-xl border border-gray-200 p-4">
                            <div className="text-sm font-semibold text-gray-900">Create suggestion</div>
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              <div>
                                <div className="text-xs font-semibold text-gray-700">Section</div>
                                <select
                                  value={newSuggestionSectionId}
                                  onChange={(e) => setNewSuggestionSectionId(e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                >
                                  <option value="">Select section…</option>
                                  {Object.entries(SECTION_METADATA)
                                    .filter(([id]) => id !== 'cover-page' && id !== 'table-of-contents')
                                    .map(([id, meta]) => (
                                      <option key={id} value={id}>{meta.title}</option>
                                    ))}
                                </select>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-700">Comment (optional)</div>
                                <input
                                  value={newSuggestionComment}
                                  onChange={(e) => setNewSuggestionComment(e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                  placeholder="Why this change?"
                                />
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-700">Original text (optional)</div>
                                <textarea
                                  value={newSuggestionOriginalText}
                                  onChange={(e) => setNewSuggestionOriginalText(e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                  rows={3}
                                />
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-gray-700">Suggested text</div>
                                <textarea
                                  value={newSuggestionSuggestedText}
                                  onChange={(e) => setNewSuggestionSuggestedText(e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                  rows={3}
                                />
                              </div>
                            </div>
                            <div className="mt-3 flex justify-end">
                              <button
                                type="button"
                                onClick={() => void createSuggestion()}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                                disabled={suggestionsLoading || !newSuggestionSectionId || !newSuggestionSuggestedText.trim()}
                              >
                                Create
                              </button>
                            </div>
                          </div>

                          <div className="mt-6">
                            {suggestionsLoading ? (
                              <div className="py-8 text-sm text-gray-600">Loading…</div>
                            ) : suggestions.length === 0 ? (
                              <div className="py-8 text-sm text-gray-600">No suggestions.</div>
                            ) : (
                              <div className="space-y-3">
                                {suggestions.map((s: BusinessPlanSuggestionRow) => {
                                  const when = s.created_at ? new Date(s.created_at).toLocaleString() : '-';
                                  const author = s.author_user_id ? String(s.author_user_id).slice(0, 8) : '-';
                                  const title = (SECTION_METADATA as any)?.[s.section_id]?.title || s.section_id;

                                  return (
                                    <div key={s.id} className="rounded-xl border border-gray-200 p-4">
                                      <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0">
                                          <div className="text-sm font-semibold text-gray-900">{title}</div>
                                          <div className="mt-1 text-xs text-gray-500">{when} · {author} · {s.status}</div>
                                          {s.comment ? <div className="mt-2 text-sm text-gray-700">{s.comment}</div> : null}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {s.status === 'open' ? (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => void applySuggestion(s)}
                                                disabled={!canEditPlan}
                                                className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
                                              >
                                                Apply
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => void rejectSuggestion(s)}
                                                disabled={!canEditPlan}
                                                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-gray-50 disabled:opacity-50"
                                              >
                                                Reject
                                              </button>
                                            </>
                                          ) : null}
                                        </div>
                                      </div>

                                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                                        <div className="rounded-lg bg-gray-50 p-3">
                                          <div className="text-xs font-semibold text-gray-600">Original</div>
                                          <div className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{s.original_text || '-'}</div>
                                        </div>
                                        <div className="rounded-lg bg-gray-50 p-3">
                                          <div className="text-xs font-semibold text-gray-600">Suggested</div>
                                          <div className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{s.suggested_text || '-'}</div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-gray-500">No plan loaded.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <BusinessPlanHubHeader 
          plansCount={savedPlans.length}
          ideasCount={savedIdeas.length}
          showNewPlanButton={!(activeTab === 'plans' && savedPlans.length === 0)}
        />

        {showGettingStarted ? (
          <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-blue-900">Getting started</div>
                <div className="mt-1 text-sm text-blue-800">
                  Build an investor-ready plan in a few minutes. Follow the steps below.
                </div>
                <ol className="mt-3 space-y-2 text-sm text-blue-900">
                  <li className="flex gap-2">
                    <span className="font-semibold">1.</span>
                    <span>
                      Pick a template (optional) in <span className="font-semibold">Templates</span>
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">2.</span>
                    <span>
                      Choose an idea in <span className="font-semibold">Ideas</span> and click <span className="font-semibold">Create Plan</span>
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">3.</span>
                    <span>
                      Use <span className="font-semibold">Generate Plan</span>, then export to PDF/DOCX
                    </span>
                  </li>
                </ol>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => handleTabChange('templates')}
                    className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-blue-700 shadow-sm ring-1 ring-blue-200 transition hover:bg-blue-50"
                  >
                    Browse templates
                  </button>
                  <button
                    onClick={() => handleTabChange('ideas')}
                    className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-blue-700 shadow-sm ring-1 ring-blue-200 transition hover:bg-blue-50"
                  >
                    Choose an idea
                  </button>
                  <button
                    onClick={() => {
                      try {
                        window.localStorage.setItem('bp_hub_getting_started_dismissed', 'true');
                      } catch {
                      }
                      setShowGettingStarted(false);
                    }}
                    className="rounded-lg px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                  >
                    Dismiss
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  try {
                    window.localStorage.setItem('bp_hub_getting_started_dismissed', 'true');
                  } catch {
                  }
                  setShowGettingStarted(false);
                }}
                className="rounded-md p-2 text-blue-700 transition hover:bg-blue-100"
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        ) : null}

        {/* Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6" aria-label="Tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                    ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                  {tab.id === 'plans' && savedPlans.length > 0 && (
                    <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-600">
                      {savedPlans.length}
                    </span>
                  )}
                  {tab.id === 'ideas' && savedIdeas.length > 0 && (
                    <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                      {savedIdeas.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <ActiveTabComponent />
          </div>
        </div>

        {/* Command Palette Hint */}
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowCommandPalette(true)}
            data-tooltip-id="bp-command-palette"
            data-tooltip-content="Quick actions, search plans/ideas, and navigation"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm transition hover:bg-gray-50"
          >
            <span>Press</span>
            <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold">⌘K</kbd>
            <span>for quick actions</span>
          </button>
          <Tooltip id="bp-command-palette" place="top" />
        </div>
      </div>

      {/* Command Palette */}
      <BusinessPlanCommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onNavigate={(tabId: string) => {
          handleTabChange(tabId as TabId);
          setShowCommandPalette(false);
        }}
        savedPlans={savedPlans}
        savedIdeas={savedIdeas}
      />
    </div>
  );
};

export default BusinessPlanHub;
